import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Users, BookOpen, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import headerImage from "@/assets/headers/header-quem-somos.jpg";
import trabalhosIcon from "@/assets/trabalhos-icon.png";

const QuemSomos = () => {
  const { t } = useTranslation();

  return (
    <Layout>
      <SEO 
        title={t('aboutUs.title')}
        description={t('aboutUs.intro1')}
        keywords="tupja orlando, terreiro pai joaquim, história umbanda orlando, missão terreiro, acolhimento espiritual, primeira visita terreiro"
        canonicalUrl={`${window.location.origin}/quem-somos`}
      />
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${headerImage})` }}
        />
        <div className="absolute inset-0 bg-logo-brown-dark/70" />
        <div className="relative z-10 text-center px-4 container mx-auto">
          <h1 className="font-display text-5xl md:text-7xl text-logo-beige mb-6 font-bold">
            {t('aboutUs.title')}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto font-body">
            {t('aboutUs.subtitle')}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          
          <div className="space-y-6 text-foreground/80 leading-relaxed mb-16 font-body text-lg">
            <p className="first-letter:text-5xl first-letter:font-display first-letter:text-secondary first-letter:mr-2 first-letter:float-left">
              {t('aboutUs.intro1')}
            </p>
            
            <p>{t('aboutUs.intro2')}</p>
            <p>{t('aboutUs.intro3')}</p>

            <div className="bg-card p-8 rounded-2xl border-2 border-spiritual/20 shadow-lg my-12">
              <h3 className="font-display text-3xl text-primary mb-6 font-bold flex items-center gap-3">
                <Heart className="w-8 h-8 text-primary" />
                {t('aboutUs.firstVisit.title')}
              </h3>
              
              <div className="space-y-4">
                <p>{t('aboutUs.firstVisit.text1')}</p>
                <p>{t('aboutUs.firstVisit.text2')}</p>
                <p>{t('aboutUs.firstVisit.text3')}</p>
              </div>
            </div>

            <div className="bg-card p-8 rounded-2xl border-2 border-spiritual/20 shadow-lg">
              <h3 className="font-display text-3xl text-primary mb-6 font-bold flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-primary" />
                {t('aboutUs.mission.title')}
              </h3>
              
              <div className="space-y-4">
                <p>{t('aboutUs.mission.text1')}</p>
                <p>{t('aboutUs.mission.text2')}</p>
                <p className="font-display text-2xl text-primary mt-8 italic">
                  {t('aboutUs.mission.closing')}
                </p>
              </div>
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-secondary to-transparent my-16" />

          {/* Cards Section */}
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            <Card className="p-8 border-2 border-logo-brown/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-card to-background flex flex-col h-full">
              <div className="w-16 h-16 rounded-full bg-logo-brown flex items-center justify-center mb-4 shadow-lg overflow-hidden">
                <img src={trabalhosIcon} alt={t('aboutUs.cards.spiritualWorks.title')} className="w-full h-full object-cover" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-4 font-bold">
                {t('aboutUs.cards.spiritualWorks.title')}
              </h3>
              <p className="text-foreground/70 font-body flex-grow">
                {t('aboutUs.cards.spiritualWorks.desc')}
              </p>
              <Link to="/trabalhos-espirituais" className="mt-6">
                <Button variant="default" className="w-full font-display shadow-lg">
                  {t('aboutUs.cards.learnMore')}
                </Button>
              </Link>
            </Card>

            <Card className="p-8 border-2 border-logo-brown/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-card to-background flex flex-col h-full">
              <div className="w-16 h-16 rounded-full bg-logo-brown flex items-center justify-center mb-4 shadow-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-4 font-bold">
                {t('aboutUs.cards.houseRules.title')}
              </h3>
              <p className="text-foreground/70 font-body flex-grow">
                {t('aboutUs.cards.houseRules.desc')}
              </p>
              <Link to="/normas" className="mt-6">
                <Button variant="default" className="w-full font-display shadow-lg">
                  {t('aboutUs.cards.learnMore')}
                </Button>
              </Link>
            </Card>

            <Card className="p-8 border-2 border-logo-brown/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-gradient-to-br from-card to-background flex flex-col h-full">
              <div className="w-16 h-16 rounded-full bg-logo-brown flex items-center justify-center mb-4 shadow-lg">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-4 font-bold">
                {t('aboutUs.cards.donations.title')}
              </h3>
              <p className="text-foreground/70 font-body flex-grow">
                {t('aboutUs.cards.donations.desc')}
              </p>
              <Link to="/doacoes" className="mt-6">
                <Button variant="default" className="w-full font-display shadow-lg">
                  {t('aboutUs.cards.learnMore')}
                </Button>
              </Link>
            </Card>
          </div>

        </div>
      </section>
    </div>
    </Layout>
  );
};

export default QuemSomos;